import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import ApiLink from 'API';

// Fullscreen image viewer (no auto-download). Fetches the protected image as a
// blob once, shows it contained on a dark backdrop, with a Save button that
// reuses the same blob. Click backdrop or X to close.
export default function ImageLightbox({ roomId, messageId, fileName, onClose }) {
    const [url, setUrl] = useState('');
    const [error, setError] = useState(false);
    const blobRef = useRef(null);
    const objUrlRef = useRef('');

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${ApiLink}/api/chat/file/${roomId}/${messageId}`, {
                withCredentials: true,
                headers: { token: localStorage.getItem('token') },
                responseType: 'blob',
            })
            .then((res) => {
                if (cancelled) return;
                blobRef.current = res.data;
                const obj = URL.createObjectURL(res.data);
                objUrlRef.current = obj;
                setUrl(obj);
            })
            .catch(() => !cancelled && setError(true));
        return () => {
            cancelled = true;
            if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current);
        };
    }, [roomId, messageId]);

    // Close on Escape
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const save = (e) => {
        e.stopPropagation();
        if (!blobRef.current) return;
        const a = document.createElement('a');
        a.href = objUrlRef.current;
        a.download = fileName || 'image';
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    return (
        <Box
            onClick={onClose}
            sx={{
                position: 'fixed', inset: 0, zIndex: 14000,
                bgcolor: 'rgba(0,0,0,0.92)',
                display: 'flex', flexDirection: 'column',
            }}
        >
            {/* Top bar */}
            <Stack
                direction="row" alignItems="center" justifyContent="space-between"
                sx={{ p: 1.5, color: '#fff' }}
                onClick={(e) => e.stopPropagation()}
            >
                <Typography variant="body2" noWrap sx={{ maxWidth: '60%', opacity: 0.85 }}>{fileName}</Typography>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="ບັນທຶກ (Save)">
                        <IconButton onClick={save} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' }} disabled={!url}>
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="ປິດ (Close)">
                        <IconButton onClick={onClose} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' }}>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {/* Image area */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, minHeight: 0 }}>
                {error ? (
                    <Typography color="#fff">ໂຫລດຮູບບໍ່ສຳເລັດ</Typography>
                ) : !url ? (
                    <CircularProgress sx={{ color: '#fff' }} />
                ) : (
                    <Box
                        component="img"
                        src={url}
                        alt={fileName}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 1 }}
                    />
                )}
            </Box>
        </Box>
    );
}
